import type { TaxCategoryOption } from '../api/taxCategory/taxCategoryApiSlice';

/** Why a price cannot be entered net — shown beside the field. */
export type NetEntryBlock = 'no_category' | 'mixed_categories';

export type PriceTaxRate =
    | { ratePercent: number, block?: never }
    | { ratePercent: null, block: NetEntryBlock };

/**
 * The one VAT rate a price is entered at, from the tax categories of the items
 * it prices: today's rate in the tenant's country, as the backend resolves it
 * (`GET /tax-category` › `currentRatePercent`).
 *
 * Several ids are for a price list several products share, and give one rate
 * only when they agree. A missing category blocks net entry; it never falls
 * back to the tenant's default, which is a prefill for new items only.
 */
export const rateOf = (
    ids: ReadonlyArray<string>,
    categories: ReadonlyArray<Pick<TaxCategoryOption, 'id' | 'currentRatePercent'>>,
): PriceTaxRate => {
    if (ids.length === 0 || ids.some((id) => !id)) return { ratePercent: null, block: 'no_category' };
    const rates = new Set(ids.map((id) => categories.find((category) => category.id === id)?.currentRatePercent ?? null));
    if (rates.has(null)) return { ratePercent: null, block: 'no_category' };
    const [only, ...rest] = Array.from(rates) as number[];
    return rest.length > 0 ? { ratePercent: null, block: 'mixed_categories' } : { ratePercent: only };
};

/**
 * The tax categories of everything priced from one price list. A list can be
 * shared, and net entry needs every user of it to agree on the rate
 * (`rateOf`). A user without a known category contributes `null`, which blocks
 * rather than being skipped: its customers pay the same gross.
 */
export const priceListTaxCategoryIds = (
    priceCategoryId: string,
    users: ReadonlyArray<{ priceCategoryId: string | null, taxCategoryId: string | null }>,
): Array<string | null> =>
    users.filter((user) => user.priceCategoryId === priceCategoryId).map((user) => user.taxCategoryId);
